import styled from 'styled-components';
import { Box, Tooltip } from '@material-ui/core';
import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import {
  ARTICLE_IDS,
  SEARCH_DATE_FORMAT,
  TRACKING
} from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import * as LighthouseService from 'common/services/lighthouse.service';
import Icon from 'components/base/Icon';
import {
  getProjectsPeriodsOptions,
  PROJECTS,
  updatedAt
} from '../services/projects.services';
import ShareMenuButton from 'components/ShareMenuButton';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import {
  CAN_EDIT_ACCOUNT_TYPES,
  FEATURE_FLAGS_NAMES
} from '@linearb/linjs-common';
import { LINE_HEIGHT } from 'common/constants/sizes';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import Dropdown from 'components/base/Dropdown';
import TabList from 'components/TabList';
import AppRouter from 'common/AppRouter';
import { pluralize } from 'common/utils/utils';
import RangePicker from 'components/base/RangePicker';
import { momentDateValidator } from 'common/utils/time.utils';
import moment from 'moment';
import { CUSTOM_RANGE_MIN_DATE, SUMMARY_PERIODS } from '../projects.consts';
import useSettingsService from 'components/hooks/useSettingsService';
import useLastUpdatedValue from '../hooks/useLastUpdatedValue';
import { InfoCircleIcon, lightColorPalette } from '@linearb/linui-common';
import { LBButton } from '@linearb/buzz';

const CreateButtonWrapper = styled(Flex)`
  padding-left: 0.5rem;
`;

const TitleSection = () => (
  <Flex items='center'>
    <Text size='x-x-large' padding='0.5rem 0'>
      Projects
    </Text>
    <Padding r='small'>
      <LBButton
        onClick={() => LighthouseService.navigateArticle(ARTICLE_IDS.PROJECTS)}
        size='icon'
        variant='ghost'
      >
        <Icon name='helpicon' size='smaller' />
      </LBButton>
    </Padding>
  </Flex>
);

const ProjectsTopBar = ({
  summaryData,
  isCanConfigProjects,
  selectedPeriod,
  setSelectedPeriod,
  onCreateClick,
  favoriteProjectsCount,
  history,
  selectedTimeRange,
  setSelectedTimeRange
}) => {
  const { start_date, end_date } = selectedTimeRange;
  const { getSettingOrDefinitionValue } = useSettingsService();

  const lastUpdated = useLastUpdatedValue();

  const pdtDateRangePickerEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.pdtDateRangePickerEnable
  });
  const {
    icon: projectsIcon,
    value: projectsValue,
    name: projectsTitle
  } = _.get(summaryData, '[0]', {});
  const {
    icon: peopleIcon,
    value: peopleValue,
    name: peopleTitle
  } = _.get(summaryData, '[1]', {});
  const tabs = [
    {
      name: `${projectsValue} ${projectsTitle}`,
      path: AppRouter.PROJECTS_API_ALL.path,
      icon: projectsIcon
    },
    favoriteProjectsCount
      ? {
          name: pluralize(favoriteProjectsCount, 'Favorite'),
          path: AppRouter.PROJECTS_API_FAVORITES.path,
          icon: 'star'
        }
      : null
  ].filter(Boolean);
  const selectedTab =
    // eslint-disable-next-line no-restricted-globals
    tabs.find(({ path }) => location.pathname.includes(path)) || {};
  return (
    <Flex
      pl='medium'
      pr='medium'
      items='center'
      height='5.7rem'
      justify='space-between'
      data-cy='report-topBar'
    >
      <Flex grow='1'>
        <Flex items='center'>
          <TitleSection />
          <Flex pr='small' key='periodPicker'>
            <Dropdown
              width='auto'
              height='2rem'
              options={getProjectsPeriodsOptions().filter(
                ({ value }) =>
                  pdtDateRangePickerEnabled || value !== SUMMARY_PERIODS.custom
              )}
              selectedValue={selectedPeriod}
              onChange={setSelectedPeriod}
              data-cy='period-picker'
            />
          </Flex>
          {pdtDateRangePickerEnabled && selectedPeriod === 'C' && (
            <Flex width='18rem' pr='small'>
              <RangePicker
                minDate={CUSTOM_RANGE_MIN_DATE}
                height='2.5rem'
                isOutsideRange={day => day.isAfter(moment(), 'day')}
                startDate={momentDateValidator(start_date)}
                endDate={momentDateValidator(end_date)}
                onDatesChange={({ endDate, startDate }) => {
                  const newTimeframe = {
                    end_date: moment(endDate).format(SEARCH_DATE_FORMAT),
                    start_date: moment(startDate).format(SEARCH_DATE_FORMAT)
                  };
                  setSelectedTimeRange(newTimeframe);
                }}
              />
            </Flex>
          )}
          <Padding b='small'>
            <TabList
              showUnderline={false}
              fontSize='medium'
              tabs={tabs}
              onTabClick={({ path }) => {
                path === AppRouter.PROJECTS_API_FAVORITES.path &&
                  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                    page: TRACKING.PAGES.PROJECTS,
                    click_type: TRACKING.CLICK.FAVORITE_PROJECTS_TAB
                  });
                history.push(path);
              }}
              selectedAttributeName='path'
              selectedTab={selectedTab.path}
            />
          </Padding>
        </Flex>
      </Flex>
      <Padding r='small'>
        <Flex
          items='center'
          pr='small'
          borderRight={`1px solid ${getColor('catskillWhite')}`}
        >
          <Icon name={peopleIcon} color='shark' />
          <Text size='medium' pl='0.5rem'>
            {peopleValue}
          </Text>
          <Text size='medium' pl='0.5rem' color='shuttleGray'>
            {peopleTitle}
          </Text>
        </Flex>
      </Padding>
      {lastUpdated && (
        <Box display='flex' alignItems='center' paddingRight={1}>
          <Padding
            r='small'
            key='updated'
            data-testid='last-updated-data-testid'
          >
            <Text
              color={PROJECTS.SECONDARY_COLOR}
              lineHeight={LINE_HEIGHT.small}
            >
              Updated: {updatedAt(lastUpdated)}
            </Text>
          </Padding>
          <Tooltip
            title={
              <div>
                Data updates automatically whenever a project is <br /> added or
                modified, and at the end of each iteration.
              </div>
            }
          >
            <div>
              <InfoCircleIcon color={lightColorPalette.lbTextSubdued} />
            </div>
          </Tooltip>
        </Box>
      )}
      {withPermissions({
        WrappedComponent: (
          <ShareMenuButton
            variant='outline'
            pngFileName={`Projects-${new Date().toISOString()}`}
            onClick={() => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.PROJECTS,
                click_type: TRACKING.CLICK.DOWNLOAD_AS_PNG
              });
            }}
          />
        ),
        requiredPermissions: CAN_EDIT_ACCOUNT_TYPES
      })}
      {withPermissions({
        WrappedComponent: isCanConfigProjects && (
          <CreateButtonWrapper data-html2canvas-hide='true'>
            <LBButton
              variant='outline'
              onClick={() => {
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.PROJECTS,
                  click_type: TRACKING.CLICK.CREATE_PROJECT
                });
                onCreateClick();
              }}
            >
              Create Project
            </LBButton>
          </CreateButtonWrapper>
        ),
        requiredPermissions: CAN_EDIT_ACCOUNT_TYPES
      })}
    </Flex>
  );
};

export default ProjectsTopBar;
